/**
 * Test Chat Modal Component
 * 
 * Modal for testing chatbot conversations with:
 * - Real-time chat interface
 * - Message history
 * - Typing indicators
 * - Error handling
 * - Response metadata display
 * 
 * Design Reference: Based on chat interface with modal wrapper
 */

import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Send, Bot, User, MessageSquare, X, RefreshCw, ChevronDown, ChevronUp, Shield, Search, Database, Sparkles } from 'lucide-react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import type { Chatbot } from '../../types'
import { logger } from '../../utils/logger'
import { cleanChatbotResponse } from '../../utils/responseFormatter'
import { API_BASE_URL } from '../../utils/constants'

// Variable config format (matches VariablesSection)
interface VariableConfig {
  default?: string
  required?: boolean
  auto_filter?: boolean
  auto_train?: boolean
}

type VariableValue = string | VariableConfig

const TEST_CHAT_VARIABLES_STORAGE_PREFIX = 'bgai:test-chat:variables'

const getVariableStorageKey = (chatbotId: string) => `${TEST_CHAT_VARIABLES_STORAGE_PREFIX}:${chatbotId}`

const readStoredVariableValues = (chatbotId: string): Record<string, string> => {
  if (typeof window === 'undefined') return {}

  try {
    const raw = window.localStorage.getItem(getVariableStorageKey(chatbotId))
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}

    return Object.entries(parsed).reduce<Record<string, string>>((acc, [key, value]) => {
      if (typeof value === 'string') acc[key] = value
      return acc
    }, {})
  } catch {
    return {}
  }
}

const writeStoredVariableValues = (chatbotId: string, values: Record<string, string>) => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(getVariableStorageKey(chatbotId), JSON.stringify(values))
  } catch {
    // Browser storage can be unavailable in private mode or if quota is full.
  }
}

/**
 * Props interface for TestChatModal
 */
interface TestChatModalProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Function to close the modal */
  onClose: () => void
  /** Chatbot to test */
  chatbot: Chatbot | null
}

/**
 * Message interface for UI
 */
interface UIMessage {
  id: string
  content: string
  role: 'user' | 'assistant'
  timestamp: string
  tool_calls?: Array<{ tool: string; args?: Record<string, any> }>
  metadata?: {
    sources_used?: number
    confidence?: number
    processing_time_ms?: number
    enhanced_query?: string
    requires_human_handoff?: boolean
  }
}

/**
 * TestChatModal Component
 * 
 * Interactive chat interface for testing chatbots
 */
export const TestChatModal: React.FC<TestChatModalProps> = ({
  isOpen,
  onClose,
  chatbot
}) => {
  // ====================================
  // STATE MANAGEMENT
  // ====================================

  const [messages, setMessages] = useState<UIMessage[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showVariables, setShowVariables] = useState(true)
  const [variableValues, setVariableValues] = useState<Record<string, string>>({})
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [isCreatingSession, setIsCreatingSession] = useState(false)
  const [activeTools, setActiveTools] = useState<string[]>([])

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const skipNextVariablePersistRef = useRef(false)

  // Extract variables from chatbot settings
  const botVariables = useMemo(() => {
    const vars = (chatbot as any)?.settings?.variables || {}
    return Object.entries(vars).map(([name, value]) => {
      const config = typeof value === 'string'
        ? { default: value, required: false, auto_filter: false, auto_train: false }
        : value as VariableConfig
      return { name, ...config }
    })
  }, [chatbot])

  const hasVariables = botVariables.length > 0
  const requiredVariables = botVariables.filter(v => v.required)

  // ====================================
  // EFFECTS
  // ====================================

  /**
   * Scroll to bottom when new messages arrive
   */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  /**
   * Focus input when modal opens
   */
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  /**
   * Reset chat when chatbot changes
   */
  useEffect(() => {
    if (chatbot && isOpen) {
      handleClearChat()
      const storedValues = readStoredVariableValues(chatbot.id)
      const defaults: Record<string, string> = {}
      botVariables.forEach(v => {
        defaults[v.name] = storedValues[v.name] ?? v.default ?? ''
      })
      skipNextVariablePersistRef.current = true
      setVariableValues(defaults)
    }
  }, [chatbot?.id, isOpen, botVariables])

  /**
   * Persist test variable values locally so reopening the modal keeps them.
   */
  useEffect(() => {
    if (!chatbot || !isOpen || !hasVariables) return
    if (skipNextVariablePersistRef.current) {
      skipNextVariablePersistRef.current = false
      return
    }
    writeStoredVariableValues(chatbot.id, variableValues)
  }, [chatbot?.id, hasVariables, isOpen, variableValues])

  const handleVariableChange = (name: string, value: string) => {
    setVariableValues(prev => ({ ...prev, [name]: value }))
    setSessionId(null)
  }

  // ====================================
  // CHAT HANDLERS
  // ====================================

  /**
   * Create a widget session with variables
   */
  const createSession = async (): Promise<string | null> => {
    if (!chatbot) return null

    // Build variables object (only include non-empty values)
    const varsToSend: Record<string, string> = {}
    Object.entries(variableValues).forEach(([key, val]) => {
      if (val?.trim()) {
        varsToSend[key] = val.trim()
      }
    })

    try {
      const response = await fetch(`${API_BASE_URL}/widget/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatbot_id: chatbot.id,
          visitor_id: `test-${Date.now()}`,
          visitor_name: 'Test User',
          // Variables go inside customer_metadata
          customer_metadata: Object.keys(varsToSend).length > 0 ? { variables: varsToSend } : undefined
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
        throw new Error(typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail))
      }

      const data = await response.json()
      return data.session_id
    } catch (err: any) {
      console.error('Failed to create session:', err)
      throw new Error(err.message || 'Failed to create session')
    }
  }

  /**
   * Send a message to the chatbot using widget API
   */
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !chatbot || isLoading) {
      return
    }

    // Validate required variables
    const missingRequired = requiredVariables.filter(v => !variableValues[v.name]?.trim())
    if (missingRequired.length > 0) {
      setError(`Missing required variables: ${missingRequired.map(v => v.name).join(', ')}`)
      return
    }

    const userMessage: UIMessage = {
      id: `user-${Date.now()}`,
      content: inputMessage.trim(),
      role: 'user',
      timestamp: new Date().toISOString()
    }

    // Add user message immediately
    setMessages(prev => [...prev, userMessage])
    setInputMessage('')
    setSending(true)
    setError(null)

    try {
      // Create session if not exists
      let currentSessionId = sessionId
      if (!currentSessionId) {
        setIsCreatingSession(true)
        currentSessionId = await createSession()
        setSessionId(currentSessionId)
        setIsCreatingSession(false)
      }

      if (!currentSessionId) {
        throw new Error('Failed to create chat session')
      }

      logger.debug('Sending test message via widget API', {
        component: 'TestChatModal',
        action: 'send_message',
        chatbotId: chatbot.id,
        sessionId: currentSessionId,
        messageLength: userMessage.content.length
      })

      // Always use streaming — all bots stream by default
      {
        const streamUrl = `${API_BASE_URL}/widget/chat/message/stream`
        const ac = new AbortController()
        const timeout = setTimeout(() => ac.abort(), 300_000) // 5-minute timeout
        const streamResp = await fetch(streamUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: currentSessionId,
            message: userMessage.content,
          }),
          signal: ac.signal,
        })
        clearTimeout(timeout)

        if (!streamResp.ok) {
          throw new Error(`Stream failed: ${streamResp.status}`)
        }

        // Create a placeholder assistant message that will be updated
        const assistantId = `assistant-${Date.now()}`
        const placeholderMsg: UIMessage = {
          id: assistantId,
          content: '',
          role: 'assistant',
          timestamp: new Date().toISOString(),
        }
        setMessages(prev => [...prev, placeholderMsg])

        // Read the SSE stream
        const reader = streamResp.body!.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let fullText = ''
        let reasoning = ''
        const toolCalls: Array<{ tool: string; args?: Record<string, any> }> = []

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })

          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const payload = line.slice(6).trim()
            if (payload === '[DONE]') continue

            let event: any
            try { event = JSON.parse(payload) } catch { continue }

            if (event.type === 'text-delta') {
              fullText += event.delta || ''
              // Update the placeholder message with accumulated text
              setMessages(prev =>
                prev.map(m =>
                  m.id === assistantId ? { ...m, content: fullText } : m
                )
              )
            } else if (event.type === 'reasoning-delta') {
              reasoning += event.delta || ''
            } else if (event.type === 'tool-call') {
              const toolName = event.toolName || event.tool_name || 'unknown'
              toolCalls.push({ tool: toolName, args: event.input || event.args })
              setActiveTools(prev => [...prev, toolName])
            } else if (event.type === 'error') {
              throw new Error(event.errorText || 'Stream error')
            }
          }
        }

        setActiveTools([])

        // Final update with metadata and tool calls
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantId
              ? {
                  ...m,
                  content: fullText,
                  tool_calls: toolCalls.length > 0 ? toolCalls : undefined,
                  metadata: { ...(reasoning ? { enhanced_query: `Thinking: ${reasoning.slice(0, 200)}...` } : {}) }
                }
              : m
          )
        )
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message'
      
      logger.error('Chat message failed', {
        component: 'TestChatModal',
        action: 'send_error',
        chatbotId: chatbot.id,
        error: errorMessage
      })

      setError(errorMessage)
      
      // Add error message to chat
      const errorChatMessage: UIMessage = {
        id: `error-${Date.now()}`,
        content: `❌ Error: ${errorMessage}`,
        role: 'assistant',
        timestamp: new Date().toISOString()
      }
      
      setMessages(prev => [...prev, errorChatMessage])
    } finally {
      setSending(false)
    }
  }

  /**
   * Handle Enter key press
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  /**
   * Clear chat history and reset session
   */
  const handleClearChat = () => {
    setMessages([])
    setError(null)
    setSessionId(null) // Reset session so new variables can be used

    logger.debug('Chat history cleared', {
      component: 'TestChatModal',
      action: 'clear_chat',
      chatbotId: chatbot?.id
    })
  }

  /**
   * Handle modal close
   */
  const handleClose = () => {
    setMessages([])
    setInputMessage('')
    setError(null)
    setSessionId(null) // Reset session
    onClose()

    logger.debug('Test chat modal closed', {
      component: 'TestChatModal',
      action: 'modal_close',
      chatbotId: chatbot?.id,
      messageCount: messages.length
    })
  }


  // ====================================
  // RENDER HELPERS
  // ====================================

  /**
   * Render chat message
   */
  const renderMessage = (message: UIMessage) => {
    const isUser = message.role === 'user'
    
    return (
      <div
        key={message.id}
        className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div className={`flex max-w-[80%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
          {/* Avatar */}
          <div className={`flex-shrink-0 ${isUser ? 'ml-3' : 'mr-3'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isUser ? 'bg-blue-100' : 'bg-green-100'
            }`}>
              {isUser ? (
                <User className="w-4 h-4 text-blue-600" />
              ) : (
                <Bot className="w-4 h-4 text-green-600" />
              )}
            </div>
          </div>
          
          {/* Message Content */}
          <div className={`rounded-lg px-4 py-2 ${
            isUser 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-100 text-gray-900'
          }`}>
            {isUser ? (
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
            ) : (
              <div 
                className="text-sm whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: cleanChatbotResponse(message.content) }}
              />
            )}
            
            {/* Tool usage badges */}
            {!isUser && message.tool_calls && message.tool_calls.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {message.tool_calls.map((tc, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">
                    <Sparkles className="w-3 h-3 mr-1" />
                    {tc.tool}
                  </span>
                ))}
              </div>
            )}

            {/* Metadata for assistant messages */}
            {!isUser && message.metadata && (
              <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                {message.metadata.processing_time_ms !== undefined && message.metadata.processing_time_ms > 0 && (
                  <div>⏱️ {message.metadata.processing_time_ms}ms</div>
                )}
                {message.metadata.sources_used !== undefined && (
                  <div>Sources: {message.metadata.sources_used}</div>
                )}
                {message.metadata.confidence !== undefined && (
                  <div>Confidence: {message.metadata.confidence}</div>
                )}
                {message.metadata.requires_human_handoff && (
                  <div className="text-orange-600 font-medium">⚠️ Human handoff recommended</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  /**
   * Render empty state
   */
  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center h-64 text-gray-500">
      <MessageSquare className="w-12 h-12 mb-4" />
      <h3 className="text-lg font-medium mb-2">Start Testing</h3>
      <p className="text-sm text-center max-w-md">
        Send a message to test how <strong>{chatbot?.name}</strong> responds. This is a safe environment to try different questions and scenarios.
      </p>
    </div>
  )

  /**
   * Render active tool indicator during streaming
   */
  const renderActiveToolIndicator = () => {
    if (activeTools.length === 0) return null

    return (
      <div className="flex justify-start mb-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-xs text-purple-700">
          <Sparkles className="w-3 h-3 animate-pulse" />
          Running {activeTools[activeTools.length - 1]}...
        </div>
      </div>
    )
  }

  /**
   * Render loading indicator
   */
  const renderTypingIndicator = () => {
    if (!isLoading) return null

    return (
      <div className="flex justify-start mb-4">
        <div className="flex mr-3">
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <Bot className="w-4 h-4 text-green-600" />
          </div>
        </div>
        <div className="bg-gray-100 rounded-lg px-4 py-2">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    )
  }

  // ====================================
  // RENDER
  // ====================================

  // Don't render if no chatbot selected
  if (!chatbot) {
    return null
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      size="xl"
      title={`Test Chat: ${chatbot.name}`}
      description="Test your chatbot's responses in a safe environment"
      showCloseButton={true}
    >
      <div className="flex flex-col h-96">
        {/* Chat Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Bot className="w-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{chatbot.name}</h3>
              <p className="text-sm text-gray-500 capitalize">
                {chatbot.industry?.replace('_', ' ') || 'General'}
              </p>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearChat}
            disabled={messages.length === 0}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Clear
          </Button>
        </div>

        {/* Session Variables Section */}
        {hasVariables && (
          <div className="border-b border-gray-200 bg-gray-50">
            <button
              onClick={() => setShowVariables(!showVariables)}
              className="w-full px-4 py-2 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              <span className="flex items-center gap-2">
                <span>Session Variables</span>
                <span className="text-xs text-gray-500 font-normal">
                  ({botVariables.length} defined{requiredVariables.length > 0 ? `, ${requiredVariables.length} required` : ''})
                </span>
              </span>
              {showVariables ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showVariables && (
              <div className="px-4 pb-3 grid grid-cols-2 gap-3">
                {botVariables.map(variable => (
                  <div key={variable.name} className="flex flex-col">
                    <label className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                      <span className="font-mono text-blue-600">{`{{${variable.name}}}`}</span>
                      {variable.required && (
                        <span title="Required">
                          <Shield className="w-3 h-3 text-red-500" />
                        </span>
                      )}
                      {variable.auto_filter && (
                        <span title="Auto Filter">
                          <Search className="w-3 h-3 text-purple-500" />
                        </span>
                      )}
                      {variable.auto_train && (
                        <span title="Auto Train">
                          <Database className="w-3 h-3 text-green-500" />
                        </span>
                      )}
                    </label>
                    <Input
                      value={variableValues[variable.name] || ''}
                      onChange={e => handleVariableChange(variable.name, e.target.value)}
                      placeholder={variable.default || `Enter ${variable.name}...`}
                      className={`text-sm ${variable.required && !variableValues[variable.name]?.trim() ? 'border-red-300' : ''}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            renderEmptyState()
          ) : (
            <>
              {messages.map(renderMessage)}
              {renderActiveToolIndicator()}
              {renderTypingIndicator()}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 bg-red-50 border-t border-red-200">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Chat Input */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0">
          <div className="flex space-x-3">
            <Input
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={isLoading}
              className="flex-1"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default TestChatModal
